import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiDocument, uiGhostReadout, uiHeader, uiPill } from "../ui/kit.js";

/**
 * Threads — the shared "CEO's correspondence" view used by Acquisitions
 * and Government Relations.
 *
 * Left: your threads (each with its current step) and new items (pitches,
 * invitations). Right: the open item. A thread shows its whole story in
 * order — your replies and the outcomes as short entries, then the current
 * letter in full with its decisions. Choosing a decision adds your reply to
 * the thread at once, runs the action through /api/actions/execute, and the
 * thread re-renders in place with the next letter. Nothing ever jumps to a
 * different item on its own.
 *
 * thUI(main, opts) where opts = {
 *   title, subtitle, summary: [{label, value}], load: () => data,
 *   threadsOf(data), itemsOf(data) (new items: documents), itemsTitle,
 *   afterAction(action, data) -> id to select (optional), emptyText,
 *   top(data) -> HTML above the desk (optional), footer(data) -> below it,
 *   decorate(readEl, selected, data) -> add panels to the open item (optional),
 * }
 */

const TH_STATE = {};  // per screen: {sel: {type, id}}

function thSteps(t) {
  if (!t.steps || !t.step) return "";
  return `<ol class="th-steps">${t.steps.map((s, i) =>
    `<li class="${i + 1 < t.step ? "done" : i + 1 === t.step ? "on" : ""}"><i>${i + 1 < t.step ? "✓" : i + 1}</i><span>${esc(s)}</span></li>`).join("")}</ol>`;
}

function thEntry(e) {
  if (e.kind_entry === "doc") return uiDocument(e);
  if (e.kind_entry === "qa") {
    return `<div class="th-qa ${esc(e.tone || "")}">
      <div class="th-reply-head"><b>${esc(e.who)} asked</b><span>Month ${e.month}</span></div>
      <p class="th-q">“${esc(e.question)}”</p>
      <p class="th-a"><b>You (${esc(e.style)}):</b> ${esc(e.answer)}</p>
      <p class="th-r">${esc(e.reaction)}</p></div>`;
  }
  return `<div class="th-reply ${esc(e.tone || "")} ${e.who === "You" ? "mine" : ""}">
    <div class="th-reply-head"><b>${esc(e.who)}</b><span>Month ${e.month}</span></div><p>${esc(e.text)}</p></div>`;
}

function thListItem(key, t, on) {
  const tone = { open: "info", merging: "warn", done: "good", ended: "", active: "info", signed: "good", finalized: "good", rejected: "" }[t.status] || "";
  return `<button type="button" class="th-item ${on ? "on" : ""} ${t.needs_you ? "urgent" : ""}" data-th-open="${esc(key)}">
    <i></i><span><b>${esc(t.title)}</b><em>${esc(t.label)}</em></span>${uiPill(t.status_label || t.status, tone)}</button>`;
}

async function thUI(main, opts) {
  const st = TH_STATE[opts.key] = TH_STATE[opts.key] || { sel: null };
  const data = await opts.load();
  const threads = opts.threadsOf(data);
  const items = opts.itemsOf(data);
  const find = (sel) => sel && (sel.type === "thread" ? threads.find((t) => t.id === sel.id) : items.find((d) => d.id === sel.id));
  if (!find(st.sel)) {
    const first = threads.find((t) => t.needs_you) || (items[0] && { item: items[0] }) || threads[0];
    st.sel = first ? (first.item ? { type: "item", id: first.item.id } : { type: "thread", id: first.id }) : null;
  }
  const sel = find(st.sel);

  main.innerHTML = `
    <div class="sx th">
      ${uiHeader(opts.title, opts.subtitle)}
      <div class="mx-summary">${opts.summary(data).map((s) => `<div><span>${esc(s.label)}</span><b>${s.value}</b></div>`).join("")}</div>
      ${opts.top ? opts.top(data) : ""}
      <div class="desk">
        <aside class="desk-list">
          <div><div class="desk-group-title"><span>${esc(opts.threadsTitle)}</span><span>${threads.length}</span></div>
            ${threads.map((t) => thListItem(`thread:${t.id}`, t, st.sel && st.sel.type === "thread" && st.sel.id === t.id)).join("")
              || `<div class="lx-foot">${esc(opts.emptyThreads)}</div>`}</div>
          <div><div class="desk-group-title"><span>${esc(opts.itemsTitle)}</span><span>${items.length}</span></div>
            ${items.map((d) => `<button type="button" class="desk-item ${st.sel && st.sel.type === "item" && st.sel.id === d.id ? "on" : ""}" data-th-open="item:${esc(d.id)}">
              <i></i><span><b>${esc(d.subject)}</b><em>${esc(d.from_name)} · ${esc(d.from_title)}</em></span></button>`).join("")
              || `<div class="lx-foot">${esc(opts.emptyItems)}</div>`}</div>
        </aside>
        <section class="desk-read" id="th-read">${!sel ? `<div class="desk-empty">${esc(opts.emptyText)}</div>`
          : st.sel.type === "thread" ? `
            <div class="th-head"><h3>${esc(sel.title)}</h3>${uiPill(sel.label, sel.needs_you ? "info" : "")}</div>
            ${thSteps(sel)}
            <div class="th-entries">${sel.entries.map(thEntry).join("")}</div>`
          : uiDocument(sel)}</section>
      </div>
      ${opts.footer ? opts.footer(data) : ""}
    </div>`;

  // a screen can add its own panels to the open item (Government: the country file and the payment plan)
  if (opts.decorate) opts.decorate(main.querySelector("#th-read"), sel, data);

  main.querySelectorAll("[data-th-open]").forEach((b) => b.addEventListener("click", () => {
    const [type, ...rest] = b.dataset.thOpen.split(":");
    st.sel = { type, id: rest.join(":") };
    thUI(main, opts);
  }));

  const read = main.querySelector("#th-read");
  read.querySelectorAll(".doc-decision").forEach((b) => b.addEventListener("click", async () => {
    const doc = b.closest(".doc");
    const result = doc.querySelector("[data-doc-result]");
    const action = b.dataset.docAction ? JSON.parse(b.dataset.docAction) : null;
    const client = b.dataset.docClient || "";
    if (client === "dismiss") {
      (opts.onDismiss || (() => {}))(st.sel.id, data);
      st.sel = null;
      return thUI(main, opts);
    }
    if (client === "forecast") {
      result.textContent = "Your team is playing 12 months forward on a copy of the company…";
      try { result.innerHTML = uiGhostReadout(await postJSON("/forecast", { action, months: 12 })); }
      catch (e) { result.textContent = e.message; }
      return;
    }
    if (client.startsWith("converse:") && opts.onConverse) {
      return opts.onConverse(client.slice(9), () => (typeof window.refreshAll === "function" ? window.refreshAll() : thUI(main, opts)));
    }
    if (!action) return;
    if (b.dataset.confirm && !window.confirm(b.dataset.confirm)) return;
    // Your reply shows right under the buttons, in the same letter: the layout doesn't move under you.
    read.querySelectorAll(".doc-decision").forEach((x) => { x.disabled = true; });
    b.classList.add("chosen");
    doc.querySelectorAll(".doc-choice").forEach((c) => c.classList.toggle("faded", !c.contains(b)));
    const pending = document.createElement("div");
    pending.className = "th-reply mine pending";
    pending.innerHTML = `<div class="th-reply-head"><b>You</b><span>now</span></div><p>${esc(b.querySelector("b").textContent)}…</p>`;
    result.appendChild(pending);
    if (pending.scrollIntoView) pending.scrollIntoView({ behavior: "smooth", block: "nearest" });
    let message;
    try {
      message = (await postJSON("/actions/execute", { action })).message;
    } catch (e) {
      pending.classList.add("bad");
      pending.querySelector("p").textContent = e.message;
      read.querySelectorAll(".doc-decision").forEach((x) => { x.disabled = false; });
      b.classList.remove("chosen");
      doc.querySelectorAll(".doc-choice").forEach((c) => c.classList.remove("faded"));
      return;
    }
    pending.classList.remove("pending");
    pending.querySelector("p").textContent = message;
    const next = opts.afterAction ? await opts.afterAction(action, st.sel) : null;
    if (next) st.sel = next;
    // refreshAll re-renders this tab (and the top bar) with the selection kept.
    setTimeout(() => (typeof window.refreshAll === "function" ? window.refreshAll() : thUI(main, opts)), 700);
  }));
}

export { thUI };
